import { useState, useRef, useEffect, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Download, FileText, Printer, Eye, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import { saveAs } from "file-saver";

// Locations configuration
const LOCATIONS = [
  { id: "siete-palmas", code: "siete-palmas", name: "Siete Palmas - Av. Pintor Felo Monzón, 2" },
  { id: "lomo-frailes", code: "lomo-frailes", name: "Lomo Los Frailes - Calle Jordán, 42" },
];

// Sticker dimensions in mm (7x10 cm = 70x100 mm)
const STICKER_WIDTH_MM = 70;
const STICKER_HEIGHT_MM = 100;
const STICKERS_PER_ROW = 2;
const STICKERS_PER_COL = 2;
const STICKERS_PER_PAGE = STICKERS_PER_ROW * STICKERS_PER_COL;

// For high-resolution canvas (300 DPI equivalent)
const SCALE_FACTOR = 4;
const STICKER_WIDTH_PX = 280 * SCALE_FACTOR;
const STICKER_HEIGHT_PX = 400 * SCALE_FACTOR;

// Brand colors - Mesa (Classic)
const CHOCOLATE_BROWN = "#4B2E1E";
const CREAM_WHITE = "#F7F5F0";
const GOLD_ACCENT = "#C9A227";

// Brand colors - Para Llevar (Blue variant)
const TAKEAWAY_BLUE = "#1E3A5F";
const TAKEAWAY_CREAM = "#F0F4F7";
const TAKEAWAY_ACCENT = "#3B82F6";

// QR Types for anti-fraud system
type QRType = "mesa" | "pickup";

interface QRPrintCenterProps {
  token: string;
}

const QRPrintCenter = ({ token }: QRPrintCenterProps) => {
  const { toast } = useToast();
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [qrType, setQrType] = useState<QRType>("mesa");
  const [startTable, setStartTable] = useState<number>(1);
  const [endTable, setEndTable] = useState<number>(10);
  const [previewTable, setPreviewTable] = useState<number>(1);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingZIP, setIsGeneratingZIP] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const selectedLocationData = LOCATIONS.find((l) => l.id === selectedLocation);
  
  // PRODUCTION DOMAIN - Fixed URL for QR codes (independent of development environment)
  const PRODUCTION_DOMAIN = "https://pasteleriareyes.com";
  const baseUrl = PRODUCTION_DOMAIN;

  // Generate QR URL based on type (mesa or pickup)
  const generateQRUrl = (locationCode: string, tableNumber: number) => {
    if (qrType === "pickup") {
      return `${baseUrl}/carta?store=${locationCode}&type=pickup`;
    }
    return `${baseUrl}/carta?store=${locationCode}&mesa=${tableNumber}`;
  };

  // Draw sticker on canvas
  const drawSticker = useCallback(
    async (
      ctx: CanvasRenderingContext2D,
      tableNumber: number,
      locationName: string,
      locationCode: string,
      width: number,
      height: number,
      stickerType: QRType = "mesa"
    ): Promise<void> => {
      const isPickup = stickerType === "pickup";
      
      // Color scheme based on sticker type
      const primaryColor = isPickup ? TAKEAWAY_BLUE : CHOCOLATE_BROWN;
      const bgColor = isPickup ? TAKEAWAY_CREAM : CREAM_WHITE;
      const accentColor = isPickup ? TAKEAWAY_ACCENT : GOLD_ACCENT;
      
      const padding = width * 0.05;
      const borderWidth = width * 0.02;

      // Background
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, width, height);

      // Add subtle texture pattern
      ctx.globalAlpha = 0.03;
      for (let i = 0; i < 50; i++) {
        const x = Math.random() * width;
        const y = Math.random() * height;
        const size = Math.random() * 20 + 5;
        ctx.fillStyle = "#888";
        ctx.beginPath();
        ctx.ellipse(x, y, size, size * 0.5, Math.random() * Math.PI, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      // Ornamental border frame
      ctx.strokeStyle = primaryColor;
      ctx.lineWidth = borderWidth;
      ctx.strokeRect(padding, padding, width - padding * 2, height - padding * 2);

      // Inner decorative border
      ctx.lineWidth = borderWidth * 0.4;
      ctx.strokeRect(
        padding + borderWidth * 2,
        padding + borderWidth * 2,
        width - (padding + borderWidth * 2) * 2,
        height - (padding + borderWidth * 2) * 2
      );

      // Corner decorations (artisanal style)
      const cornerSize = width * 0.08;
      const cornerOffset = padding + borderWidth * 0.5;
      ctx.fillStyle = primaryColor;

      // Draw corner flourishes
      const drawCornerFlourish = (cx: number, cy: number, rotation: number) => {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(rotation);
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.bezierCurveTo(cornerSize * 0.5, 0, cornerSize, cornerSize * 0.3, cornerSize, cornerSize);
        ctx.lineTo(cornerSize * 0.8, cornerSize);
        ctx.bezierCurveTo(cornerSize * 0.8, cornerSize * 0.5, cornerSize * 0.4, 0.1, 0, 0.1);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      };

      drawCornerFlourish(cornerOffset, cornerOffset, 0);
      drawCornerFlourish(width - cornerOffset, cornerOffset, Math.PI / 2);
      drawCornerFlourish(width - cornerOffset, height - cornerOffset, Math.PI);
      drawCornerFlourish(cornerOffset, height - cornerOffset, -Math.PI / 2);

      // Header text - different for each type
      const headerY = height * 0.12;
      ctx.fillStyle = primaryColor;
      ctx.font = `bold ${width * 0.08}px 'Playfair Display', serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      if (isPickup) {
        // Para Llevar header
        ctx.fillText("PARA LLEVAR", width / 2, headerY);
        
        // Subtitle with icon effect
        ctx.font = `600 ${width * 0.055}px 'Lato', sans-serif`;
        ctx.fillText("🛍️ TAKE AWAY", width / 2, height * 0.20);
      } else {
        // Mesa header
        ctx.fillText("MESA Nº", width / 2, headerY);
        
        // Large table number
        ctx.font = `bold ${width * 0.18}px 'Playfair Display', serif`;
        ctx.fillText(tableNumber.toString(), width / 2, height * 0.22);
      }

      // Location name with accent line
      const locationY = height * 0.30;
      ctx.strokeStyle = accentColor;
      ctx.lineWidth = 2;
      const lineWidth = width * 0.4;
      ctx.beginPath();
      ctx.moveTo((width - lineWidth) / 2, locationY);
      ctx.lineTo((width + lineWidth) / 2, locationY);
      ctx.stroke();

      ctx.fillStyle = primaryColor;
      ctx.font = `500 ${width * 0.035}px 'Lato', sans-serif`;
      ctx.fillText(locationName, width / 2, locationY + width * 0.05);

      // QR Code - draw as image from SVG
      const qrSize = width * 0.45;
      const qrX = (width - qrSize) / 2;
      const qrY = height * 0.38;

      // Create QR code as data URL
      const qrUrl = generateQRUrl(locationCode, tableNumber);
      
      // Use a temporary canvas to render QR
      const qrCanvas = document.createElement("canvas");
      qrCanvas.width = qrSize;
      qrCanvas.height = qrSize;
      const qrCtx = qrCanvas.getContext("2d")!;
      
      // Draw white background for QR
      qrCtx.fillStyle = "#FFFFFF";
      qrCtx.fillRect(0, 0, qrSize, qrSize);

      // Get QR code from existing element or create inline
      await new Promise<void>((resolve) => {
        const img = new Image();
        // For pickup, use a special ID; for mesa, use table number
        const qrId = isPickup ? `qr-pickup` : `qr-${tableNumber}`;
        const svgElement = document.getElementById(qrId);
        if (svgElement) {
          const svgData = new XMLSerializer().serializeToString(svgElement);
          const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
          const url = URL.createObjectURL(svgBlob);
          img.onload = () => {
            qrCtx.drawImage(img, 0, 0, qrSize, qrSize);
            URL.revokeObjectURL(url);
            resolve();
          };
          img.onerror = () => resolve();
          img.src = url;
        } else {
          // Fallback: create QR pattern placeholder
          qrCtx.fillStyle = primaryColor;
          qrCtx.fillRect(qrSize * 0.1, qrSize * 0.1, qrSize * 0.8, qrSize * 0.8);
          resolve();
        }
      });

      ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

      // QR border
      ctx.strokeStyle = primaryColor;
      ctx.lineWidth = 3;
      ctx.strokeRect(qrX - 5, qrY - 5, qrSize + 10, qrSize + 10);

      // Bottom text - Instructions (different per type)
      const bottomY = height * 0.76;
      ctx.fillStyle = primaryColor;
      ctx.font = `600 ${width * 0.04}px 'Lato', sans-serif`;
      
      if (isPickup) {
        ctx.fillText("Escanea con tu móvil", width / 2, bottomY);
        ctx.font = `400 ${width * 0.035}px 'Lato', sans-serif`;
        ctx.fillText("para pedir y recoger aquí", width / 2, bottomY + width * 0.05);
      } else {
        ctx.fillText("Escanea con tu móvil", width / 2, bottomY);
        ctx.font = `400 ${width * 0.035}px 'Lato', sans-serif`;
        ctx.fillText("para ver la carta y pedir aquí", width / 2, bottomY + width * 0.05);
      }

      // Brand footer - adjusted position with more padding from bottom border
      const footerY = height * 0.86;
      ctx.strokeStyle = accentColor;
      ctx.lineWidth = 1.5;
      const footerLineWidth = width * 0.3;
      ctx.beginPath();
      ctx.moveTo((width - footerLineWidth) / 2, footerY - width * 0.015);
      ctx.lineTo((width + footerLineWidth) / 2, footerY - width * 0.015);
      ctx.stroke();

      ctx.fillStyle = primaryColor;
      ctx.font = `italic ${width * 0.038}px 'Playfair Display', serif`;
      ctx.fillText("Pastelería Reyes", width / 2, footerY + width * 0.018);
      ctx.font = `300 ${width * 0.022}px 'Lato', sans-serif`;
      ctx.fillText("Desde 1979", width / 2, footerY + width * 0.048);
    },
    [generateQRUrl]
  );

  // Generate preview
  useEffect(() => {
    if (!selectedLocationData || !previewCanvasRef.current) return;

    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = STICKER_WIDTH_PX / 2;
    canvas.height = STICKER_HEIGHT_PX / 2;

    drawSticker(
      ctx,
      previewTable,
      selectedLocationData.name,
      selectedLocationData.code,
      canvas.width,
      canvas.height,
      qrType
    );
  }, [selectedLocationData, previewTable, drawSticker, qrType]);

  // Generate PDF with multiple stickers per page
  const generatePDF = async () => {
    if (!selectedLocationData) {
      toast({
        title: "Error",
        description: "Selecciona una sede primero",
        variant: "destructive",
      });
      return;
    }

    if (startTable > endTable) {
      toast({
        title: "Error",
        description: "El número inicial debe ser menor al final",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingPDF(true);

    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const tables = [];
      for (let i = startTable; i <= endTable; i++) {
        tables.push(i);
      }

      const pageWidth = 210;
      const pageHeight = 297;
      const marginX = (pageWidth - STICKER_WIDTH_MM * STICKERS_PER_ROW) / 2;
      const marginY = (pageHeight - STICKER_HEIGHT_MM * STICKERS_PER_COL) / 2;

      for (let i = 0; i < tables.length; i++) {
        const pageIndex = Math.floor(i / STICKERS_PER_PAGE);
        const positionOnPage = i % STICKERS_PER_PAGE;
        const col = positionOnPage % STICKERS_PER_ROW;
        const row = Math.floor(positionOnPage / STICKERS_PER_ROW);

        if (i > 0 && positionOnPage === 0) {
          pdf.addPage();
        }

        const canvas = document.createElement("canvas");
        canvas.width = STICKER_WIDTH_PX;
        canvas.height = STICKER_HEIGHT_PX;
        const ctx = canvas.getContext("2d")!;

        await drawSticker(
          ctx,
          tables[i],
          selectedLocationData.name,
          selectedLocationData.code,
          canvas.width,
          canvas.height,
          qrType
        );

        const imgData = canvas.toDataURL("image/png", 1.0);
        const x = marginX + col * STICKER_WIDTH_MM;
        const y = marginY + row * STICKER_HEIGHT_MM;

        pdf.addImage(imgData, "PNG", x, y, STICKER_WIDTH_MM, STICKER_HEIGHT_MM);
      }

      const filePrefix = qrType === "pickup" ? "QR_ParaLlevar" : "QR_Mesas";
      pdf.save(`${filePrefix}_${selectedLocationData.code}_${startTable}-${endTable}.pdf`);

      toast({
        title: "PDF generado",
        description: `Se han generado ${tables.length} adhesivos para impresión`,
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast({
        title: "Error",
        description: "No se pudo generar el PDF",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Generate ZIP with individual PNG files
  const generateZIP = async () => {
    if (!selectedLocationData) {
      toast({
        title: "Error",
        description: "Selecciona una sede primero",
        variant: "destructive",
      });
      return;
    }

    if (startTable > endTable) {
      toast({
        title: "Error",
        description: "El número inicial debe ser menor al final",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingZIP(true);

    try {
      const zip = new JSZip();

      for (let tableNum = startTable; tableNum <= endTable; tableNum++) {
        const canvas = document.createElement("canvas");
        canvas.width = STICKER_WIDTH_PX;
        canvas.height = STICKER_HEIGHT_PX;
        const ctx = canvas.getContext("2d")!;

        await drawSticker(
          ctx,
          tableNum,
          selectedLocationData.name,
          selectedLocationData.code,
          canvas.width,
          canvas.height,
          qrType
        );

        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((b) => resolve(b!), "image/png", 1.0);
        });

        const filePrefix = qrType === "pickup" ? "ParaLlevar" : "Mesa";
        zip.file(`${filePrefix}_${tableNum.toString().padStart(2, "0")}_${selectedLocationData.code}.png`, blob);
      }

      const content = await zip.generateAsync({ type: "blob" });
      const packPrefix = qrType === "pickup" ? "QR_ParaLlevar_Pack" : "QR_Pack";
      saveAs(content, `${packPrefix}_${selectedLocationData.code}_${startTable}-${endTable}.zip`);

      toast({
        title: "ZIP generado",
        description: `Se han generado ${endTable - startTable + 1} imágenes PNG`,
      });
    } catch (error) {
      console.error("Error generating ZIP:", error);
      toast({
        title: "Error",
        description: "No se pudo generar el ZIP",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingZIP(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5" />
            Configuración de Adhesivos QR
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Location selector */}
          <div className="space-y-2">
            <Label htmlFor="location">Seleccionar Sede</Label>
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger id="location">
                <SelectValue placeholder="Elige una sede" />
              </SelectTrigger>
              <SelectContent>
                {LOCATIONS.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* QR Type selector */}
          <div className="space-y-2">
            <Label htmlFor="qrType">Tipo de QR</Label>
            <Select value={qrType} onValueChange={(v) => setQrType(v as QRType)}>
              <SelectTrigger id="qrType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mesa">🍽️ QR de Mesa (pedido en local)</SelectItem>
                <SelectItem value="pickup">🛍️ QR Para Llevar (mostrador)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {qrType === "mesa" 
                ? "Genera QRs para mesas numeradas. Los clientes podrán pedir desde su mesa."
                : "Genera un QR para el mostrador. Los clientes podrán pedir para llevar."}
            </p>
          </div>

          {/* Table range - only show for mesa type */}
          {qrType === "mesa" && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startTable">Mesa Inicial</Label>
                <Input
                  id="startTable"
                  type="number"
                  min={1}
                  max={50}
                  value={startTable === 0 ? '' : startTable}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    if (rawValue === '') {
                      setStartTable(0);
                      return;
                    }
                    const val = parseInt(rawValue);
                    if (!isNaN(val)) {
                      setStartTable(val);
                    }
                  }}
                  onBlur={(e) => {
                    let val = parseInt(e.target.value) || 1;
                    val = Math.max(1, Math.min(50, val));
                    setStartTable(val);
                    if (val > previewTable || previewTable > endTable) {
                      setPreviewTable(val);
                    }
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endTable">Mesa Final</Label>
                <Input
                  id="endTable"
                  type="number"
                  min={1}
                  max={50}
                  value={endTable === 0 ? '' : endTable}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    if (rawValue === '') {
                      setEndTable(0);
                      return;
                    }
                    const val = parseInt(rawValue);
                    if (!isNaN(val)) {
                      setEndTable(val);
                    }
                  }}
                  onBlur={(e) => {
                    let val = parseInt(e.target.value) || 10;
                    val = Math.max(1, Math.min(50, val));
                    setEndTable(val);
                    if (previewTable > val) {
                      setPreviewTable(val);
                    }
                  }}
                />
              </div>
            </div>
          )}
          
          {/* Info for pickup type */}
          {qrType === "pickup" && (
            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                🛍️ Los QR "Para Llevar" son idénticos para cada sede. Solo necesitas generar uno por mostrador.
              </p>
            </div>
          )}

          {/* Preview table selector - only for mesa type */}
          {selectedLocation && qrType === "mesa" && (
            <div className="space-y-2">
              <Label htmlFor="previewTable">Vista Previa - Mesa Nº</Label>
              <Input
                id="previewTable"
                type="number"
                min={startTable}
                max={endTable}
                value={previewTable}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || startTable;
                  setPreviewTable(Math.max(startTable, Math.min(endTable, val)));
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Live Preview */}
      {selectedLocation && selectedLocationData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Vista Previa del Adhesivo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center gap-4">
              {/* Hidden QR for canvas rendering */}
              <div className="hidden">
                {qrType === "pickup" ? (
                  <QRCodeSVG
                    id="qr-pickup"
                    value={generateQRUrl(selectedLocationData.code, 0)}
                    size={256}
                    level="H"
                    includeMargin={false}
                  />
                ) : (
                  Array.from({ length: endTable - startTable + 1 }, (_, i) => startTable + i).map(
                    (tableNum) => (
                      <QRCodeSVG
                        key={tableNum}
                        id={`qr-${tableNum}`}
                        value={generateQRUrl(selectedLocationData.code, tableNum)}
                        size={256}
                        level="H"
                        includeMargin={false}
                      />
                    )
                  )
                )}
              </div>

              {/* Preview canvas */}
              <div className="border-4 border-muted rounded-lg overflow-hidden shadow-lg">
                <canvas
                  ref={previewCanvasRef}
                  className="max-w-full h-auto"
                  style={{ maxHeight: "400px" }}
                />
              </div>

              <p className="text-sm text-muted-foreground text-center">
                Tamaño real: 7 × 10 cm • Resolución: 300 DPI equivalente
              </p>
              
              {/* URL codificada para verificación visual */}
              <div className="mt-3 p-3 bg-muted/50 rounded-lg border text-center">
                <p className="text-xs text-muted-foreground mb-1">
                  Enlace codificado en el QR:
                </p>
                <code className="text-xs font-mono text-primary break-all">
                  {generateQRUrl(selectedLocationData.code, qrType === "pickup" ? 0 : previewTable)}
                </code>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export buttons */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Exportar Adhesivos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              onClick={generatePDF}
              disabled={!selectedLocation || isGeneratingPDF}
              className="w-full h-16"
              size="lg"
            >
              <FileText className="h-5 w-5 mr-2" />
              {isGeneratingPDF ? "Generando PDF..." : "Generar PDF para Impresión"}
            </Button>

            <Button
              onClick={generateZIP}
              disabled={!selectedLocation || isGeneratingZIP}
              variant="outline"
              className="w-full h-16"
              size="lg"
            >
              <Package className="h-5 w-5 mr-2" />
              {isGeneratingZIP ? "Generando ZIP..." : "Descargar Pack PNG"}
            </Button>
          </div>

          <p className="text-sm text-muted-foreground">
            • PDF: Documento A4 con {STICKERS_PER_PAGE} adhesivos por página, listo para imprimir
            <br />• ZIP: Imágenes individuales en alta resolución (PNG)
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default QRPrintCenter;
